import { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, DollarSign, Users, Download, Calendar,
  ArrowUp, ArrowDown, Package, CheckCircle2, Clock,
  AlertCircle, FileSpreadsheet, CreditCard, Banknote, Star, MessageSquare,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND = "#0d9488";
const ACCENT = "#f59e0b";
const METHOD_COLORS = { Stripe: "#635bff", "Bank Transfer": "#0d9488" };

const AnalyticsManager = () => {
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30");
  const [chartView, setChartView] = useState("revenue");
  const [exporting, setExporting] = useState(false);

  const { getAuthHeader } = useAuth();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const h = getAuthHeader();
      const [a, r, f] = await Promise.all([
        axios.get(`${API}/analytics?days=${dateRange}`, { headers: h }),
        axios.get(`${API}/analytics/revenue`, { headers: h }),
        axios.get(`${API}/feedback/stats`, { headers: h }).catch(() => ({ data: null })),
      ]);
      setData(a.data);
      setRevenue(r.data);
      setFeedback(f.data);
    } catch (err) {
      console.error("Analytics load failed:", err);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [dateRange, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportOrdersCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/analytics/orders-export?days=${dateRange}`, {
        headers: getAuthHeader(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Orders CSV exported");
    } catch {
      toast.error("CSV export failed");
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const actionItems = revenue?.action_items || {};

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}><CardContent className="p-6">
            <div className="h-4 w-1/2 skeleton rounded mb-2" />
            <div className="h-8 w-3/4 skeleton rounded" />
          </CardContent></Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-lg">Business Analytics</h3>
          <p className="text-sm text-muted-foreground">Revenue, orders, and operations at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40" data-testid="analytics-range">
              <Calendar className="h-4 w-4 mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportOrdersCsv} disabled={exporting} data-testid="export-orders-csv">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> {exporting ? "Exporting..." : "Export Orders CSV"}
          </Button>
        </div>
      </div>

      {/* Action Items (Ops alerts) */}
      {(actionItems.proof_submitted > 0 || actionItems.needs_clarification > 0 || actionItems.pending_bank_transfer > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {actionItems.proof_submitted > 0 && (
            <ActionCard icon={AlertCircle} color="blue" title="Proof awaiting verification"
              value={actionItems.proof_submitted} hint="Review in Orders tab" testid="action-proof-submitted" />
          )}
          {actionItems.pending_bank_transfer > 0 && (
            <ActionCard icon={Clock} color="yellow" title="Pending bank transfers"
              value={actionItems.pending_bank_transfer} hint="Awaiting customer payment" testid="action-pending-bt" />
          )}
          {actionItems.needs_clarification > 0 && (
            <ActionCard icon={AlertCircle} color="orange" title="Need clarification"
              value={actionItems.needs_clarification} hint="Follow up with customer" testid="action-needs-clarif" />
          )}
        </div>
      )}

      {/* Period KPIs - MTD / QTD / YTD */}
      <Tabs defaultValue="mtd" className="w-full" data-testid="period-tabs">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="mtd" data-testid="tab-mtd">Month to Date</TabsTrigger>
          <TabsTrigger value="qtd" data-testid="tab-qtd">Quarter to Date</TabsTrigger>
          <TabsTrigger value="ytd" data-testid="tab-ytd">Year to Date</TabsTrigger>
        </TabsList>
        {["mtd", "qtd", "ytd"].map((p) => (
          <TabsContent key={p} value={p}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PeriodMetric icon={DollarSign} title="Revenue" prefix="$"
                value={revenue?.[p]?.revenue ?? 0}
                change={revenue?.[p]?.revenue_change ?? 0} />
              <PeriodMetric icon={Package} title="Paid Orders"
                value={revenue?.[p]?.orders ?? 0}
                change={revenue?.[p]?.orders_change ?? 0} />
              <PeriodMetric icon={TrendingUp} title="Avg Order Value" prefix="$"
                value={revenue?.[p]?.orders ? Math.round(revenue[p].revenue / revenue[p].orders) : 0} />
              <PeriodMetric icon={Users} title="vs Previous Period" suffix="%"
                value={revenue?.[p]?.revenue_change ?? 0}
                change={revenue?.[p]?.revenue_change ?? 0} hideChange />
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Rolling window summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI title={`Orders (last ${dateRange}d)`} value={summary.total_orders || 0}
          change={summary.orders_change} icon={Package} testid="kpi-orders" />
        <KPI title="Revenue" prefix="$" value={summary.total_revenue || 0}
          change={summary.revenue_change} icon={DollarSign} testid="kpi-revenue" />
        <KPI title="Avg Order" prefix="$" value={summary.avg_order_value || 0}
          icon={TrendingUp} testid="kpi-aov" />
        <KPI title="Conversion" suffix="%" value={summary.conversion_rate || 0}
          icon={CheckCircle2} testid="kpi-conversion" hint="Paid / All orders" />
        <KPI title="New Customers" value={summary.new_customers || 0}
          icon={Users} testid="kpi-customers" />
      </div>

      {/* Time-series chart */}
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base font-medium">
            {chartView === "revenue" ? "Revenue" : "Orders"} over time
          </CardTitle>
          <Select value={chartView} onValueChange={setChartView}>
            <SelectTrigger className="w-32 h-8" data-testid="chart-view-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="orders">Orders</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <LineChart data={chartView === "revenue" ? data?.revenue_by_date : data?.orders_by_date}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  formatter={(v) => chartView === "revenue" ? [`$${Number(v).toLocaleString()}`, "Revenue"] : [v, "Orders"]}
                />
                <Line
                  type="monotone"
                  dataKey={chartView === "revenue" ? "amount" : "count"}
                  stroke={chartView === "revenue" ? ACCENT : BRAND}
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Payment method split + Top courses */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Payment method split</CardTitle></CardHeader>
          <CardContent>
            {data?.payment_method_split?.length ? (
              <>
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
                    <PieChart>
                      <Pie data={data.payment_method_split} dataKey="count" nameKey="method"
                        cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {data.payment_method_split.map((e, i) => (
                          <Cell key={i} fill={METHOD_COLORS[e.method] || BRAND} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, _, p) => [`${v} orders · $${p?.payload?.revenue?.toLocaleString()}`, p?.payload?.method]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-3">
                  {data.payment_method_split.map((m) => (
                    <div key={m.method} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        {m.method === "Stripe" ? <CreditCard className="h-3.5 w-3.5" /> : <Banknote className="h-3.5 w-3.5" />}
                        {m.method}
                      </span>
                      <span className="font-medium">{m.count} · ${m.revenue.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-12 text-center">No paid orders yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Top courses by revenue</CardTitle></CardHeader>
          <CardContent>
            {data?.top_courses?.length ? (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                  <BarChart data={data.top_courses} layout="vertical" margin={{ left: 16, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="title" tick={{ fontSize: 11 }}
                      width={180} tickFormatter={(v) => v.length > 28 ? v.slice(0, 28) + "…" : v} />
                    <Tooltip contentStyle={{ fontSize: 12 }}
                      formatter={(v, _, p) => [`$${Number(v).toLocaleString()} (${p.payload.orders} orders)`, "Revenue"]} />
                    <Bar dataKey="revenue" fill={BRAND} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-12 text-center">No course sales yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Orders by format + Status breakdown + Inquiries */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Orders by format</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data?.orders_by_format?.map((i) => (
              <div key={i.format}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span>{i.format}</span>
                  <span className="font-medium">{i.count} ({i.percentage}%)</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-brand" style={{ width: `${i.percentage}%` }} />
                </div>
              </div>
            ))}
            {!data?.orders_by_format?.length && <p className="text-sm text-muted-foreground py-8 text-center">No orders yet.</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Order status breakdown</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data?.status_breakdown?.length ? data.status_breakdown.map((s) => (
              <div key={s.status} className="flex items-center justify-between text-sm">
                <Badge className={statusBadgeColor(s.status)}>{s.status.replace(/_/g, " ")}</Badge>
                <span className="font-medium">{s.count}</span>
              </div>
            )) : <p className="text-sm text-muted-foreground py-8 text-center">No orders yet.</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Inquiries</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-muted-foreground">Consulting</p>
                <p className="font-bold text-2xl text-blue-700">{data?.inquiries_summary?.consulting?.new || 0}</p>
                <p className="text-xs text-muted-foreground">new / {data?.inquiries_summary?.consulting?.total || 0} total</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <p className="text-xs text-muted-foreground">Training</p>
                <p className="font-bold text-2xl text-green-700">{data?.inquiries_summary?.training?.new || 0}</p>
                <p className="text-xs text-muted-foreground">new / {data?.inquiries_summary?.training?.total || 0} total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer feedback */}
      {feedback && feedback.total_responses > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card data-testid="feedback-overall">
            <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" /> Overall Rating</CardTitle></CardHeader>
            <CardContent className="text-center py-6">
              <p className="font-heading font-bold text-5xl">{feedback.overall_average.toFixed(1)}</p>
              <div className="flex items-center justify-center gap-0.5 my-2">
                {[1, 2, 3, 4, 5].map((r) => (
                  <Star key={r} className={`w-5 h-5 ${r <= Math.round(feedback.overall_average) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{feedback.total_responses} responses</p>
            </CardContent>
          </Card>
          <Card className="lg:col-span-2" data-testid="feedback-by-course">
            <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Average rating by course</CardTitle></CardHeader>
            <CardContent>
              {feedback.by_course.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No ratings yet.</p>
              ) : (
                <div className="space-y-3">
                  {feedback.by_course.slice(0, 6).map((c) => (
                    <div key={c.course_id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="truncate flex-1 mr-3">{c.course_title}</span>
                        <span className="font-medium whitespace-nowrap">{c.average_rating.toFixed(2)} ★ ({c.count})</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400" style={{ width: `${(c.average_rating / 5) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {feedback && feedback.recent_comments?.length > 0 && (
        <Card data-testid="feedback-comments">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><MessageSquare className="h-4 w-4 text-brand" /> Recent comments</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {feedback.recent_comments.map((c) => (
              <div key={c.id} className="border-l-2 border-amber-300 pl-3 py-1">
                <div className="flex items-center gap-2 mb-1">
                  {[1, 2, 3, 4, 5].map((r) => (
                    <Star key={r} className={`w-3 h-3 ${r <= c.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                  ))}
                  <span className="text-xs text-muted-foreground">· {c.course_title}</span>
                </div>
                <p className="text-sm">"{c.comment}"</p>
                <p className="text-xs text-muted-foreground mt-1">{new Date(c.submitted_at).toLocaleDateString()}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const statusBadgeColor = (s) => ({
  paid: "bg-green-100 text-green-800",
  pending_stripe: "bg-yellow-100 text-yellow-800",
  pending_bank_transfer: "bg-yellow-100 text-yellow-800",
  proof_submitted: "bg-blue-100 text-blue-800",
  needs_clarification: "bg-orange-100 text-orange-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-slate-200 text-slate-700",
}[s] || "bg-slate-100 text-slate-800");

const KPI = ({ title, value, change, icon: Icon, prefix = "", suffix = "", testid, hint }) => (
  <Card data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="font-heading font-bold text-2xl mt-1">
            {prefix}{typeof value === "number" ? value.toLocaleString() : value}{suffix}
          </p>
          {change !== undefined && (
            <div className={`flex items-center text-xs mt-1 ${change >= 0 ? "text-green-600" : "text-red-600"}`}>
              {change >= 0 ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
              {Math.abs(change)}% vs prev
            </div>
          )}
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
        <div className="p-2.5 rounded-full bg-slate-100"><Icon className="h-5 w-5 text-brand" /></div>
      </div>
    </CardContent>
  </Card>
);

const PeriodMetric = ({ title, value, change, icon: Icon, prefix = "", suffix = "", hideChange }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="font-heading font-bold text-3xl mt-1">
            {prefix}{typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : value}{suffix}
          </p>
          {!hideChange && change !== undefined && (
            <div className={`flex items-center text-xs mt-1 ${change >= 0 ? "text-green-600" : "text-red-600"}`}>
              {change >= 0 ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
              {Math.abs(change)}% vs prev period
            </div>
          )}
        </div>
        <div className="p-2.5 rounded-full bg-brand/10"><Icon className="h-5 w-5 text-brand" /></div>
      </div>
    </CardContent>
  </Card>
);

const ActionCard = ({ icon: Icon, color, title, value, hint, testid }) => {
  const colors = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-800",
    orange: "bg-orange-50 border-orange-200 text-orange-800",
  }[color];
  return (
    <Card className={`border ${colors}`} data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className="h-6 w-6 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs font-medium opacity-80">{title}</p>
          <p className="font-heading font-bold text-2xl">{value}</p>
          <p className="text-xs opacity-70">{hint}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalyticsManager;
